import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import api from '@/lib/api'
import { US_TIMEZONES, detectBrowserTimezone } from '@/lib/timezones'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [timezone, setTimezone] = useState(detectBrowserTimezone())
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('')
  const [orgName, setOrgName] = useState('')
  const [success, setSuccess] = useState(false)

  // Match <html>/<body> background to the shell so iOS overscroll doesn't flash
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No invitation token provided')
        setIsVerifying(false)
        return
      }

      try {
        const response = await api.get(`/api/auth/invitation/${token}`)
        const data = response.data

        if (data.valid) {
          setTokenValid(true)
          setInviteEmail(data.email)
          setInviteRole(data.role)
          setOrgName(data.organizationName)
        } else {
          setError(data.error || 'Invalid or expired invitation')
        }
      } catch {
        setError('Failed to verify invitation link')
      } finally {
        setIsVerifying(false)
      }
    }

    verifyToken()
  }, [token])

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters'
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter'
    if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter'
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Please enter your name')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const passwordError = validatePassword(password)
    if (passwordError) {
      setError(passwordError)
      return
    }

    setIsLoading(true)

    try {
      const response = await api.post(`/api/auth/invitation/${token}/accept`, {
        name: name.trim(),
        password,
        timezone,
      })
      const data = response.data

      if (data.success) {
        localStorage.setItem('app_auth_token', data.token)
        setSuccess(true)
        setTimeout(() => { window.location.href = '/' }, 1500)
      } else {
        setError(data.error || 'Failed to accept invitation')
      }
    } catch {
      setError('Failed to connect to server')
    } finally {
      setIsLoading(false)
    }
  }

  // ==========================================================================
  //   VERIFYING
  // ==========================================================================
  if (isVerifying) {
    return (
      <AuthShell
        headline={<>Hang on<br />one second.</>}
        sub="Verifying your invitation link."
      >
        <div className="flex items-center gap-3 text-white/60">
          <LoadingSpinner size="sm" />
          <p className="text-sm">Checking the invite…</p>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   INVALID TOKEN
  // ==========================================================================
  if (!tokenValid) {
    return (
      <AuthShell
        headline={<>This invite<br />has expired.</>}
        sub="Invitations are valid for 7 days. Ask your admin to send a fresh link."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Invite expired</h2>
          <p className="text-white/60 text-sm mb-6">
            {error || 'This invitation link is no longer valid.'}
          </p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-xl text-[15px] font-semibold text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97]"
          >
            Go to sign in
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   SUCCESS
  // ==========================================================================
  if (success) {
    return (
      <AuthShell
        headline={<>Welcome to<br />{orgName}.</>}
        sub="Your account is set up. Taking you to the dashboard now."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300 mb-4">
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
            Account created
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">
            You're in.
          </h2>
          <p className="text-white/60 text-sm flex items-center gap-2">
            <LoadingSpinner size="sm" />
            Taking you to your dashboard…
          </p>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   FORM
  // ==========================================================================
  const roleLabel = inviteRole === 'admin' ? 'an administrator' : `a ${inviteRole}`
  return (
    <AuthShell
      headline={<>Join<br />{orgName}.</>}
      sub={`You've been invited as ${roleLabel}. Pick a password and you're set.`}
    >
      <div
        className="opacity-0 animate-fade-in-up"
        style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
      >
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">
          Accept invite
        </h2>
        <p className="text-white/60 text-sm mb-6 break-all">
          For <span className="text-white/90">{inviteEmail}</span>
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <fieldset disabled={isLoading} className="space-y-4 border-0 p-0 m-0 min-w-0">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white/75 mb-2">
                Your name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                autoFocus
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/75 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mt-1.5 text-xs text-white/40">
                Minimum 8 characters, including uppercase, lowercase, and a number
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-white/75 mb-2">
                Confirm password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Type it again"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-white/75 mb-2">
                Time zone
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="block w-full h-12 px-4 border border-white/10 rounded-xl text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] appearance-none transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
              >
                {US_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value} className="bg-[#0d0a1f] text-white">
                    {tz.label}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-white/40">
                Booking times, reminders, and notifications display in your time zone. You can change this later.
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center h-12 px-4 text-[15px] font-semibold rounded-xl text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0d0a1f] focus:ring-brand-royal disabled:opacity-50 disabled:cursor-not-allowed transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : `Accept & join ${orgName}`}
            </button>
          </fieldset>
        </form>

        <p className="text-sm text-white/55 mt-6 pt-4 border-t border-white/10">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-brand-periwinkle hover:text-white font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  )
}
